import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchProfile, loginUser, setShowAttentionPopup } from "../../redux/authSlice";
import { getAllAppSetups } from "../../redux/appSetupSlice";
import { getAppSetupData } from "../../redux/appSetupSlice";
import {
  TextField,
  CheckboxField,
  SignatureField,
} from "../../common/FormFields";
import { PrimaryButton } from "../../common/Buttons";
import { CompanyLogo } from "../../common/SVGs";
import "./Login.css";
import { getAuditTrailData } from "../../util/getAuditTrailData";

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: "", // Pre-filled for testing
    password: "",
    rememberMe: false,
  });
  const [signatureError, setSignatureError] = useState("");
  const sigPadRef = useRef();
  const [signature, setSignature] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get signature auth methods from app setup
  const [authMethods, setAuthMethods] = useState([]);
  const [isLoadingAuthMethods, setIsLoadingAuthMethods] = useState(true);

  useEffect(() => {
    const fetchAuthMethods = async () => {
      try {
        const result = await dispatch(getAppSetupData());
        if (getAppSetupData.fulfilled.match(result)) {
          // Handle response structure: { success, message, results: [{ signature_auth_methods: [...] }] }
          const results = result.payload?.results || result.payload?.data || [];
          let authMethods = []; // Start with empty array

          if (Array.isArray(results) && results.length > 0) {
            // Get the latest app setup (first item or find by updated_at/created_at)
            const latestAppSetup = results.reduce((latest, current) => {
              const latestDate = new Date(latest.updated_at || latest.created_at || 0);
              const currentDate = new Date(current.updated_at || current.created_at || 0);
              return currentDate > latestDate ? current : latest;
            });

            if (latestAppSetup?.signature_auth_methods && Array.isArray(latestAppSetup.signature_auth_methods)) {
              authMethods = latestAppSetup.signature_auth_methods;
            }
          }

          // If no auth methods found, default to empty array (password always required, signature optional)
          setAuthMethods(authMethods);
          // Store in localStorage for other components
          localStorage.setItem("signature_auth_methods", JSON.stringify(authMethods));
        } else {
          // Default to empty array on error (password always required, signature optional)
          setAuthMethods([]);
        }
      } catch (error) {
        console.error("Error fetching app setup data:", error);
        setAuthMethods([]);
      } finally {
        setIsLoadingAuthMethods(false);
      }
    };
    fetchAuthMethods();
  }, [dispatch]);

  const { loading, error } = useSelector((state) => state.auth);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleEndDrawing = () => {
    const dataUrl = sigPadRef.current.getCanvas().toDataURL("image/png");
    setSignature(dataUrl);
    if (signatureError) {
      setSignatureError("");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Password is always required
    if (!credentials.password) {
      return;
    }

    // Only validate signature if it's in auth methods
    if (authMethods.includes("signature")) {
      if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
        setSignatureError("Signature is required");
        return;
      }
    }

    credentials.audit_trail_data = await getAuditTrailData({
      signature: authMethods.includes("signature") ? signature : "",
      action: "LOGIN",
      comment: "Log In",
      version_content: {
        signature: authMethods.includes("signature") ? signature : "",
        action_to_show: "Logged In"
      },
    });

    const resultAction = await dispatch(loginUser(credentials));
    if (loginUser.fulfilled.match(resultAction)) {
      const resp = await dispatch(fetchProfile());

      // Check if fetchProfile was rejected due to invalid token
      if (fetchProfile.rejected.match(resp)) {
        // If invalid token, redirect has already been handled in fetchProfile
        // Just return to prevent further navigation
        return;
      }

      localStorage.setItem("user_id", resp.payload.id)
      dispatch(setShowAttentionPopup(true));
      // Only proceed if fetchProfile was successful
      if (fetchProfile.fulfilled.match(resp)) {
        // Fetch app setup data and save signature_auth_methods to localStorage
        // let authMethods = null;
        // try {
        //   const appSetupResult = await dispatch(getAllAppSetups());
        //   if (getAllAppSetups.fulfilled.match(appSetupResult)) {
        //     const appSetups = appSetupResult.payload?.results || appSetupResult.payload?.data || [];
        //     if (appSetups.length > 0) {
        //       // Find the latest app setup based on updated_at or created_at
        //       const latestAppSetup = appSetups.reduce((latest, current) => {
        //         const latestDate = new Date(latest.updated_at || latest.created_at || 0);
        //         const currentDate = new Date(current.updated_at || current.created_at || 0);
        //         return currentDate > latestDate ? current : latest;
        //       });

        //       if (latestAppSetup?.signature_auth_methods) {
        //         authMethods = latestAppSetup.signature_auth_methods;
        //         // Store in separate key for backward compatibility
        //         localStorage.setItem(
        //           "signature_auth_methods",
        //           JSON.stringify(authMethods)
        //         );
        //         localStorage.setItem("application_time_zone",latestAppSetup.application_time_zone);
        //       }
        //     }
        //   }
        // } catch (error) {
        //   console.error("Error fetching app setup:", error);
        // }
        
        // Store user info with auth methods included
        const userInfoWithAuth = {
          ...resp.payload,
          signature_auth_methods: localStorage.getItem("signature_auth_methods")
        };
        localStorage.loggedinUserInfo = JSON.stringify(userInfoWithAuth);

        try {
          sessionStorage.removeItem("document_is_view_mode");
          sessionStorage.removeItem("products_use_classic_appbar");
        } catch {
          /* ignore */
        }

        // Get the redirect location from sessionStorage (stored when user was redirected to login)
        const redirectPath = sessionStorage.getItem("redirectAfterLogin");
        const redirectBasename = sessionStorage.getItem("redirectBasename");

        // Clear the stored redirect path and basename
        if (redirectPath) {
          sessionStorage.removeItem("redirectAfterLogin");
          sessionStorage.removeItem("redirectBasename");
          
          // If basename was stored and doesn't match Router basename, use window.location
          // to bypass React Router basename and go to actual URL
          if (redirectBasename && redirectBasename !== "/ri-doc-front") {
            // Use the stored basename directly (it should already have trailing slash)
            window.location.replace(redirectBasename);
          } else {
            // Navigate using React Router (works when basename matches)
            navigate(redirectPath, { replace: true });
            window.location.reload()
          }
        } else {
          // Default to home page if no redirect path was stored
          navigate("/", { replace: true });
          window.location.reload()
        }
      }
    }
  };

  return (
    <div className="login-modal-overlay">
      <div className="login-form-container">
        <div className="login-logo-container">
          <CompanyLogo />
        </div>

        <div className="login-title-container">
          <h1 className="login-title">
            Document Control and Management System
          </h1>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <TextField
            label="Username"
            type="email"
            name="email"
            value={credentials.email}
            onChange={handleChange}
            required
            width="100%"
            style={{ marginBottom: "16px" }}
            labelStyle={{
              fontSize: "12px",
              marginBottom: "4px",
              fontWeight: "500",
            }}
            inputStyle={{
              fontSize: "12px",
              padding: "8px",
              border: "1px solid #CED4DA",
              borderRadius: "4px",
            }}
          />

          <TextField
            label="Password"
            type="password"
            name="password"
            value={credentials.password}
            onChange={handleChange}
            required
            width="100%"
            style={{ marginBottom: "16px" }}
            labelStyle={{
              fontSize: "12px",
              marginBottom: "4px",
              fontWeight: "500",
            }}
            inputStyle={{
              fontSize: "12px",
              padding: "8px",
              border: "1px solid #CED4DA",
              borderRadius: "4px",
            }}
          />

          {/* Signature - Only show if signature is in auth methods */}
          {authMethods.includes("signature") && (
            <>
              <SignatureField
                ref={sigPadRef}
                label="Signature"
                required
                width="460px"
                height="100px"
                onEnd={handleEndDrawing}
              />
              {signatureError && (
                <p className="login-inline-error" role="alert">
                  {signatureError}
                </p>
              )}
            </>
          )}

          <div className="login-checkbox-container">
            <CheckboxField
              checked={credentials.rememberMe}
              onChange={(e) => {
                setCredentials((prev) => ({
                  ...prev,
                  rememberMe: e.target.checked,
                }));
              }}
              description="Remember me"
              checkboxStyle={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                accentColor: "#6c757d",
              }}
              descriptionStyle={{
                fontSize: "12px",
              }}
            />
          </div>

          <div className="login-button-container">
            <PrimaryButton
              type="submit"
              loading={loading}
              loadingText="Logging in..."
              size="sm"
            >
              Login
            </PrimaryButton>
          </div>
          {error && (
            <p className="login-inline-error login-bottom-error" role="alert">
              {error}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;

